/**
 * Что показывает раздел «Настройки».
 *
 * Сервер отдаёт настройки плоским словарём «ключ → строка» и проверяет
 * допустимые значения сам. Здесь только человеческая сторона: как поле
 * называется, в какой вкладке живёт и что изменится, если его тронуть, —
 * настройка без объяснения читается как переключатель наугад.
 *
 * Ключи и границы взяты из каталога настроек бэкенда
 * (03_dorixona_monorepo/backend/core/internal/service/settings.go).
 */
export type Tab = 'loy' | 'ord' | 'shop' | 'not' | 'gen'

export const TABS: [Tab, string][] = [
  ['loy', 'Лояльность'],
  ['ord', 'Заказы и доставка'],
  ['shop', 'Витрина'],
  ['not', 'Уведомления'],
  ['gen', 'Организация'],
]

interface BaseField {
  key: string
  tab: Tab
  label: string
  hint: string
}

/** У числового поля границы обязательны: их задаёт каталог настроек сервера. */
interface IntField extends BaseField {
  kind: 'int'
  min: number
  max: number
  /** Значение в сумах: показываем это в подписи, а не выдумываем формат. */
  money?: boolean
}

interface PlainField extends BaseField {
  kind: 'bool' | 'text'
}

export type Field = IntField | PlainField

export const FIELDS: Field[] = [
  {
    key: 'loyalty.welcome_bonus.enabled',
    tab: 'loy',
    label: 'Приветственный бонус',
    kind: 'bool',
    hint: 'Один раз на номер телефона',
  },
  {
    key: 'loyalty.welcome_bonus.amount',
    tab: 'loy',
    label: 'Размер приветственного бонуса',
    kind: 'int',
    min: 0,
    max: 100_000_000,
    money: true,
    hint: 'Сразу после регистрации',
  },
  {
    key: 'loyalty.birthday_bonus.enabled',
    tab: 'loy',
    label: 'Подарок ко дню рождения',
    kind: 'bool',
    hint: 'Тем, кто указал дату рождения',
  },
  {
    key: 'loyalty.birthday_bonus.amount',
    tab: 'loy',
    label: 'Размер подарка ко дню рождения',
    kind: 'int',
    min: 0,
    max: 100_000_000,
    money: true,
    hint: '',
  },
  {
    key: 'loyalty.birthday_bonus.days_before',
    tab: 'loy',
    label: 'За сколько дней до дня рождения начислять',
    kind: 'int',
    min: 0,
    max: 30,
    hint: '0 — в сам день рождения',
  },
  {
    key: 'loyalty.max_spend_pct',
    tab: 'loy',
    label: 'Максимум оплаты баллами, % от суммы товаров',
    kind: 'int',
    min: 0,
    max: 100,
    hint: 'Доставку баллами не оплатить',
  },
  {
    key: 'payment.online_timeout_minutes',
    tab: 'ord',
    label: 'Сколько ждём онлайн-оплату, мин',
    kind: 'int',
    min: 1,
    max: 120,
    hint: 'Потом — «оплата просрочена»',
  },
  {
    key: 'pickup.reservation_hours',
    tab: 'ord',
    label: 'Сколько часов держим бронь самовывоза',
    kind: 'int',
    min: 1,
    max: 240,
    hint: 'Потом бронь снимается',
  },
  {
    key: 'sla.new_to_assembling_minutes',
    tab: 'ord',
    label: 'Срок на подтверждение нового заказа, мин',
    kind: 'int',
    min: 0,
    max: 1440,
    hint: '0 — без ограничения',
  },
  {
    key: 'sla.assembling_to_ready_minutes',
    tab: 'ord',
    label: 'Срок на сборку, мин',
    kind: 'int',
    min: 0,
    max: 1440,
    hint: '0 — без ограничения',
  },
  {
    key: 'sla.awaiting_courier_minutes',
    tab: 'ord',
    label: 'Срок ожидания курьера, мин',
    kind: 'int',
    min: 0,
    max: 1440,
    hint: '0 — без ограничения',
  },
  {
    key: 'sla.working_hours_only',
    tab: 'ord',
    label: 'Считать сроки только в рабочие часы аптеки',
    kind: 'bool',
    hint: 'Ночью срок не идёт',
  },
  {
    key: 'delivery.stub_fee',
    tab: 'ord',
    label: 'Стоимость доставки на заглушке',
    kind: 'int',
    min: 0,
    max: 1_000_000,
    money: true,
    hint: 'Только для заглушки доставки',
  },
  {
    key: 'catalog.popular.limit',
    tab: 'shop',
    label: 'Сколько товаров на полке «Популярное»',
    kind: 'int',
    min: 1,
    max: 50,
    hint: '',
  },
  {
    key: 'catalog.popular.fallback_days',
    tab: 'shop',
    label: 'За сколько дней добирать лидеров продаж',
    kind: 'int',
    min: 1,
    max: 90,
    hint: 'Пусто — добираем лидерами продаж',
  },
  {
    key: 'catalog.low_stock_threshold',
    tab: 'shop',
    label: 'Порог «заканчивается», шт по сети',
    kind: 'int',
    min: 1,
    max: 10000,
    hint: 'Ниже — «заканчивается»',
  },
  {
    key: 'notify.sms.enabled',
    tab: 'not',
    label: 'СМС о заказах',
    kind: 'bool',
    hint: 'Клиент может отключить в профиле',
  },
  {
    key: 'notify.telegram.enabled',
    tab: 'not',
    label: 'Сообщения в Telegram',
    kind: 'bool',
    hint: 'Тем, кто вошёл через Telegram',
  },
  {
    key: 'notify.push.enabled',
    tab: 'not',
    label: 'Push в браузере',
    kind: 'bool',
    hint: '',
  },
  {
    key: 'notify.fom_sync_alert_minutes',
    tab: 'not',
    label: 'Нет синхронизации с FOM, мин',
    kind: 'int',
    min: 0,
    max: 1440,
    hint: 'Аптеке и администратору, пока аптека открыта. 0 — не слать',
  },
  {
    key: 'notify.sms.events',
    tab: 'not',
    label: 'По каким событиям слать СМС',
    kind: 'text',
    hint: 'Через запятую: order_issued, …',
  },
  {
    key: 'org.name',
    tab: 'gen',
    label: 'Название сети',
    kind: 'text',
    hint: 'Видно клиенту',
  },
  {
    key: 'org.support_phone',
    tab: 'gen',
    label: 'Телефон поддержки',
    kind: 'text',
    hint: '',
  },
  {
    key: 'org.support_email',
    tab: 'gen',
    label: 'Почта поддержки',
    kind: 'text',
    hint: '',
  },
  {
    key: 'org.registration_url',
    tab: 'gen',
    label: 'Ссылка на регистрацию',
    kind: 'text',
    hint: 'Нужна для SMS со ссылкой',
  },
  {
    key: 'platform.demo_reset_enabled',
    tab: 'gen',
    label: 'Разрешить сброс демо-данных',
    kind: 'bool',
    hint: 'Только для стенда',
  },
]

export const fieldsOf = (tab: Tab): Field[] => FIELDS.filter((f) => f.tab === tab)

/**
 * Значение поля для отправки. Числа приводим к границам, а не полагаемся на
 * отказ сервера: 400 за опечатку в поле — плохой ответ пользователю.
 */
export function normalize(field: Field, value: string): string {
  if (field.kind !== 'int') return value
  const n = Math.round(Number(value) || 0)
  return String(Math.max(field.min, Math.min(field.max, n)))
}

/** Что из черновика отличается от сохранённого — только это и отправляем. */
export function changed(
  saved: Record<string, string>,
  draft: Record<string, string>,
): Record<string, string> {
  const patch: Record<string, string> = {}
  Object.entries(draft).forEach(([key, value]) => {
    if (saved[key] !== value) patch[key] = value
  })
  return patch
}
